import {
  getFromLS,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import IconWithTooltip from "components/IconWithTooltip";
import OnOffRow from "components/OnOffRow";
import TwoOptionsRow from "components/TwoOptionsRow";
import { DISCOVERY_SHRED_TO_EXCEL_LS_KEY } from "constants/discovery";
import { modalTypes } from "constants/types";
import { ReactComponent as InfoIcon } from "icons/info-grey.svg";
import PropTypes from "prop-types";
import React, { useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { shredToExcel } from "slices/discovery/actions/byContent";

const ShredToExcelModal = ({
  documentsWithTerms,
  extensions = { wordSelected: false, pdfSelected: false },
  exportContentOptionDisabled = false,
  onExport,
  hideInheritedOptions = false,
  showNewRowOnHit = true,
  title = "Shred to Excel",
  localStorageKey = DISCOVERY_SHRED_TO_EXCEL_LS_KEY,
}) => {
  // Data options
  const [exportSelectedData, setExportSelectedData] = useState(false);
  const [splitBySentence, setSplitBySentence] = useState(false);
  const [newRowOnHit, setNewRowOnHit] = useState(false);
  const [repeatRefs, setRepeatRefs] = useState(false);
  const [displayRefsInContent, setDisplayRefsInContent] = useState(false);
  // Presentation Options
  const [showGridlines, setShowGridlines] = useState(true);
  // MS Word Specific Options
  const [wrapText, setWrapText] = useState(true);
  // PDF Specific Options
  const [pagesToIgnore, setPagesToIgnore] = useState("");
  // Advance Options
  const [textToIgnore, setTextToIgnore] = useState("");
  const [macroEnabled, setMacroEnabled] = useState(false);

  const dispatch = useDispatch();

  useLayoutEffect(() => {
    try {
      const configStringified = getFromLS(localStorageKey);

      if (!configStringified) {
        throw new Error("Shred to excel config: empty");
      }

      const config = JSON.parse(configStringified);

      setExportSelectedData(config.exportSelectedData);
      setSplitBySentence(config.splitBySentence);
      setNewRowOnHit(config.newRowOnHit);
      setRepeatRefs(config.repeatRefs);
      setDisplayRefsInContent(config.displayRefsInContent);
      setShowGridlines(config.showGridlines);
      setWrapText(config.wrapText);
      setPagesToIgnore(config.pagesToIgnore);
      setTextToIgnore(config.textToIgnore);
      setMacroEnabled(config.macroEnabled);
    } catch (e) {}
  }, [localStorageKey]);

  const dataOptions = () => (
    <div className="shred_to_excel__body__data_opts">
      <span>Data Options</span>
      {!hideInheritedOptions && (
        <>
          <TwoOptionsRow
            option1="Export all content"
            option2="Export displayed content"
            state={exportSelectedData}
            tooltipText="Export all content will output every paragraph to Excel.<br><br>Export displayed content just outputs what's in the content pane only."
            disabled={exportContentOptionDisabled}
            setState={setExportSelectedData}
          />
          <TwoOptionsRow
            option1="Split by paragraph"
            option2="Split by sentence"
            state={splitBySentence}
            tooltipText="Split by paragraph creates one row per paragraph, whereas split by sentence creates one row per sentence."
            setState={setSplitBySentence}
          />
        </>
      )}
      {showNewRowOnHit && (
        <OnOffRow
          text="New row on dictionary hit"
          state={newRowOnHit}
          tooltipText="Turning this on splits a sentence, and forces a new row once we find the dictionary term."
          setState={setNewRowOnHit}
        />
      )}

      <OnOffRow
        text="Repeat Refs"
        state={repeatRefs}
        tooltipText="Turning this on causes the ref to repeat. This continues until we see a new ref value in the Ref column."
        setState={setRepeatRefs}
      />

      <OnOffRow
        text="Display Refs in content column"
        state={displayRefsInContent}
        tooltipText="Turning this on outputs refs both in the Ref and Content columns. Leaving it off outputs the ref only in the Ref column."
        setState={setDisplayRefsInContent}
      />
    </div>
  );

  const presentationOptions = () => (
    <div className="shred_to_excel__body__presentation_opts">
      <span>Presentation Options</span>
      <OnOffRow
        text="Show gridlines"
        state={showGridlines}
        tooltipText="Turn on/off gridlines in Excel."
        setState={setShowGridlines}
      />
    </div>
  );

  const wordSpecific = () => (
    <div
      className={clsx("shred_to_excel__body__word_specific", {
        disabled: !extensions.wordSelected,
      })}
    >
      <span>MS Word Specific Options</span>
      <OnOffRow
        text="Wrap text in tables"
        state={wrapText}
        tooltipText="Turning this on wraps text in generated MS Word table cells."
        disabled={!extensions.wordSelected}
        setState={setWrapText}
      />
    </div>
  );

  const pdfSpecific = () => {
    const handleInputChange = ({ target: { value } }) => {
      const inputValue = value;
      // Remove characters other than numbers, commas, and dashes
      const sanitizedValue = inputValue.replace(/[^0-9,-]/g, "");

      setPagesToIgnore(sanitizedValue);
    };

    return (
      <div
        className={clsx("shred_to_excel__body__pdf_specific", {
          disabled: !extensions.pdfSelected,
        })}
      >
        <span>PDF Specific Options</span>
        <span className="description">
          <span>Ignore Pages</span>
          <IconWithTooltip
            icon={<InfoIcon width={11} height={11} />}
            tooltipText="Use this to ignore pages or page ranges in PDFs."
          />
        </span>
        <input
          value={pagesToIgnore}
          placeholder="E.g. 1,2,3-6,78,79"
          disabled={!extensions.pdfSelected}
          onChange={handleInputChange}
        />
      </div>
    );
  };

  const advancedOptions = () => {
    const handleTextChange = ({ target: { value } }) => {
      setTextToIgnore(value);
    };

    return (
      <div className="shred_to_excel__body__advanced_opts">
        <span>Advanced Options</span>
        <span className="description">
          <span>Ignore Text</span>
          <IconWithTooltip
            icon={<InfoIcon width={11} height={11} />}
            tooltipText="Use this to ignore certain text in your output. If the sentence contains the specified text, then the full sentence will be ignored."
          />
        </span>
        <input
          value={textToIgnore}
          placeholder="E.g. APPENDIX, COPYRIGHT"
          onChange={handleTextChange}
        />
        <OnOffRow
          text="Include macros to customize output"
          state={macroEnabled}
          tooltipText="VT Docs allows you embed macros into generated Excel outputs. Turn this on to include macros."
          setState={setMacroEnabled}
        />
      </div>
    );
  };

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.SHRED_TO_EXCEL));
  };

  const handleExportClick = () => {
    const shredOptionsRequest = {
      exportSelectedData,
      repeatRefs,
      displayRefsInContent,
      showGridlines,
      wrapText,
      pagesToIgnore,
      textToIgnore,
      macroEnabled,
      splitBySentence,
    };

    if (showNewRowOnHit) {
      shredOptionsRequest.newRowOnHit = newRowOnHit;
    }

    if (onExport) {
      onExport(shredOptionsRequest);
    } else {
      dispatch(shredToExcel(documentsWithTerms, shredOptionsRequest));
    }
  };

  return (
    <Modal
      data-testid="shred-to-excel-modal"
      className="fade-in shred_to_excel"
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b>{title}</b>
      </Modal.Header>
      <Modal.Body>
        <div className="shred_to_excel__body">
          {dataOptions()}
          {presentationOptions()}
          {wordSpecific()}
          {pdfSpecific()}
          {advancedOptions()}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          variant={NewButtonVariant.l2}
          disabled={false}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton variant={NewButtonVariant.l1} onClick={handleExportClick}>
          Export
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

ShredToExcelModal.propTypes = {
  documentsWithTerms: PropTypes.arrayOf(
    PropTypes.shape({
      docId: PropTypes.number,
      selectedTerms: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  extensions: PropTypes.shape({
    wordSelected: PropTypes.bool,
    pdfSelected: PropTypes.bool,
  }),
  exportContentOptionDisabled: PropTypes.bool,
  onExport: PropTypes.func,
  hideInheritedOptions: PropTypes.bool,
  showNewRowOnHit: PropTypes.bool,
  title: PropTypes.string,
  localStorageKey: PropTypes.string,
};

export default ShredToExcelModal;
